<template>
  <div class="history-table">
    <table class="t-table">
      <thead>
        <tr>
          <c-table-head-column
            width="20%"
            :is-selected="activeSorting.filter === SORT_FILTER.Tournament"
            :sort-filter="SORT_FILTER.Tournament"
            :sort-order="activeSorting.order"
            @toggle-sorting="onToggleSorting"
            qe-id="sort-by-tournament"
            >{{ $t('table.columns.tournament') }}</c-table-head-column
          >
          <c-table-head-column
            width="20%"
            :is-selected="activeSorting.filter === SORT_FILTER.Date"
            :sort-filter="SORT_FILTER.Date"
            :sort-order="activeSorting.order"
            @toggle-sorting="onToggleSorting"
            qe-id="sort-by-competition-end"
            >{{ $t('table.columns.date') }}</c-table-head-column
          >
          <c-table-head-column
            width="20%"
            :is-selected="activeSorting.filter === SORT_FILTER.Rank"
            :sort-filter="SORT_FILTER.Rank"
            :sort-order="activeSorting.order"
            @toggle-sorting="onToggleSorting"
            qe-id="sort-by-rank"
            >{{ $t('table.columns.rank') }}</c-table-head-column
          >
          <c-table-head-column
            width="20%"
            :is-selected="activeSorting.filter === SORT_FILTER.Score"
            :sort-filter="SORT_FILTER.Score"
            :sort-order="activeSorting.order"
            @toggle-sorting="onToggleSorting"
            qe-id="sort-by-score"
            >{{ $t('table.columns.score') }}</c-table-head-column
          >
          <c-table-head-column
            width="20%"
            :is-selected="activeSorting.filter === SORT_FILTER.TotalWin"
            :sort-filter="SORT_FILTER.TotalWin"
            :sort-order="activeSorting.order"
            @toggle-sorting="onToggleSorting"
            qe-id="sort-by-prize"
            >{{ $t('table.columns.totalWin') }}</c-table-head-column
          >
        </tr>
      </thead>
      <tbody data-qe-id="table-data">
        <template v-if="finishedRankingsRange.length > 0">
          <tr v-for="(entry, index) in finishedRankingsRange" :key="index" data-qe-id="tr-data">
            <td>
              <a
                @click="onTournamentSelect(entry.tournamentId)"
                :data-qe-id="`link-to-tournament--${entry.tournamentId}`"
                >{{ entry.tournamentName }}</a
              >
            </td>
            <td>{{ $d(new Date(entry.competitionEnd)) }}</td>
            <td>{{ entry.tournamentRank }}</td>
            <td>{{ entry.tournamentScore }}</td>
            <td>{{ entry.totalWin }}</td>
          </tr>
          <tr>
            <td :colspan="COLSPAN_NUM - 1"></td>
            <td class="accumulated-winnings">
              <span class="sum-winnings-label">{{ $t('table.zoneHistory.sumWinnings') }}</span
              ><br />
              <span data-qe-id="prize-total">{{ accumulatedWinnings }}</span>
            </td>
          </tr>
          <tr v-if="maxEntries > entriesToRender">
            <td class="show-more-row" :colspan="COLSPAN_NUM" @click="onShowMore" data-qe-id="show-more-link">
              {{ $t('table.more.button') }} <i class="icf-arrowBottom"></i>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr class="no-results" data-qe-id="no-results-available-row">
            <td :colspan="COLSPAN_NUM">{{ $t('table.more.no_results') }}</td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from '@/store'
import { State } from '@/store/state'
import { ZoneHistoryState } from '@/store/modules/ranking/zoneHistory'
import { SortFilter, Sorting } from '@/model/ranking/Sorting'
import { FinishedTournament } from '@/model/ranking/FinishedTournament'
import { Zone } from '@/model/Zone'
import CTableHeadColumn from '@/components/ranking/CTableHeadColumn.vue'
import { jslog } from '@gd/jslog'
import { track } from '@/lib/track'

const props = defineProps<{ zoneId: string }>()
const router = useRouter()
const route = useRoute()
const store = useStore()

const COLSPAN_NUM = 5
const ENTRIES_PER_BATCH = 50
const SORT_FILTER = SortFilter
const entriesToRender = ref(ENTRIES_PER_BATCH)

function created() {
  store.dispatch('ranking/zoneHistory/fetch', props.zoneId).catch((err) => {
    jslog.send({ name: 'fatal', data: { message: `could not load zone history table for: ${props.zoneId}` } })
    console.error(err)
  })
}

const zoneHistory = computed((): ZoneHistoryState => {
  return store.state.ranking.zoneHistory as ZoneHistoryState
})

const accumulatedWinnings = computed((): string => {
  return store.getters['ranking/zoneHistory/accumulatedWinnings']()
})

/**
 * Range of FinishedRankings rendered on screen
 */
const finishedRankingsRange = computed((): Array<FinishedTournament> => {
  return store.getters['ranking/zoneHistory/rankings'](0, entriesToRender.value)
})

/**
 * Stored sorting preferences containing order and filter
 */
const activeSorting = computed((): Sorting => {
  return zoneHistory.value.sorting
})

/**
 * Amount of all FinishedRankings in store
 */
const maxEntries = computed(() => {
  return zoneHistory.value.rankings.length
})

function onShowMore() {
  if (entriesToRender.value + ENTRIES_PER_BATCH <= maxEntries.value) {
    entriesToRender.value += ENTRIES_PER_BATCH
    track({
      path: route.fullPath,
      id: 'zoneHistoryTable',
      elem: 'load-more',
    })
  } else {
    entriesToRender.value = maxEntries.value
  }
}

function onTournamentSelect(tournamentId: string) {
  const zone: Zone = (store.state as State).zone
  // store.commit('setActiveTournament', tournamentId)
  router.push(`/tournamentHistory/${zone.zoneId}/${tournamentId}`)
  track({
    id: 'zoneHistoryTable',
    elem: 'tournament-select',
    path: route.fullPath,
    data: tournamentId,
  })
}

function onToggleSorting(sorting: Sorting) {
  store.dispatch('ranking/zoneHistory/toggleSortingForFilter', sorting)
  track({
    id: 'zoneHistoryTable',
    elem: 'toggle-sorting',
    path: route.fullPath,
    data: `${sorting.filter}_${sorting.order}`,
  })
}
created()
</script>
<style lang="less" scoped>
.accumulated-winnings {
  background: #f49906;
  color: white;
  font-weight: 700;
}
.sum-winnings-label {
  &:after {
    content: ':';
  }
}
</style>
